import moment from 'moment';
import { TableProps } from 'antd/es/table';
import { Button, Drawer, DrawerProps, Space, Table, TableColumnsType, Typography } from 'antd';
import { EditOutlined, ContainerOutlined, DeleteOutlined } from '@ant-design/icons';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { TeamMemberResponse } from 'store/teams/types';
import StatusTag from 'components/ui/base/status/StatusTag';
import TeamMemberTag from './TeamMemberTag';
import { User } from 'store/session/user';
import { hasPermissions } from 'config/roles/hasPermissions';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/ui/composed/formInput/FormInput';
import FormSelect from 'components/ui/composed/formSelect/FormSelect';
import { ALLOWED_NEW_MEMBER_ROLES } from 'views/teams/types';
import { omit, startCase, toLower } from 'lodash';
import { useFormik } from 'formik';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import * as Yup from 'yup';
import Notification from 'components/ui/base/notification/Notification';
import { editIndividual } from 'store/individuals/client';
import { AxiosError } from 'axios';
import { useState } from 'react';
import ConfirmTeamRoleChangeModal from 'views/teams/components/ConfirmTeamRoleChangeModal';

interface Props<T extends TeamMemberResponse> extends TableProps<T> {
    userInfo: User | undefined;
    teams: TeamMemberResponse[];
    onRemoveTeamMember?: (individualId: string) => void;
    onEditTeamMember?: () => void;
}

// Note: As we don't have a separate endpoint for team members
// and we store them in the team object, we apply client-side
// pagination and sorting
const TeamMembersTable = ({
    userInfo,
    teams,
    onRemoveTeamMember,
    onEditTeamMember,
    ...rest
}: Props<TeamMemberResponse>) => {
    const allColumns: TableColumnsType<TeamMemberResponse> = [
        {
            title: 'User Name',
            key: 'USER_NAME',
            sorter: (a, b) => (a.individual.fullName || '').localeCompare(b.individual.fullName || ''),
            render: (_, record) => {
                const dataTestId = 'members-table-user-name-row';
                return <Typography data-testid={dataTestId}>{record.individual.fullName}</Typography>;
            },
        },
        {
            title: 'Email',
            key: 'EMAIL',
            sorter: (a, b) => (a.individual.email || '').localeCompare(b.individual.email || ''),
            render: (_, record) => {
                const dataTestId = 'members-table-email-row';
                return <Typography data-testid={dataTestId}>{record.individual.email}</Typography>;
            },
        },
        {
            title: 'Role',
            key: 'ROLE',
            sorter: (a, b) => (a.role || '').localeCompare(b.role || ''),
            render: (_, record) => {
                const dataTestId = 'members-table-role-row';
                return <TeamMemberTag role={record.role} data-testid={dataTestId} />;
            },
        },
        {
            title: 'Added On',
            key: 'ADDED_ON',
            sorter: (a, b) => moment(a.individual.createdAt).valueOf() - moment(b.individual.createdAt).valueOf(),
            render: (_, record) => {
                const date = moment(record.individual.createdAt);
                const formattedDate = date.format('DD/MM/YY');
                const formattedTime = date.format('HH:mm A');
                const dataTestId = 'members-table-added-on-row';
                return (
                    <Space size={8}>
                        <span>{formattedDate}</span>
                        <Typography.Text
                            style={{
                                whiteSpace: 'nowrap',
                            }}
                            data-testid={dataTestId}
                        >
                            {formattedTime}
                        </Typography.Text>
                    </Space>
                );
            },
        },
        {
            title: 'Status',
            key: 'STATUS',
            sorter: (a, b) => {
                const statusA = a.individual.active ? 1 : 0;
                const statusB = b.individual.active ? 1 : 0;
                return statusA - statusB;
            },
            render: (_, record) => {
                const dataTestId = 'members-table-status-row';
                const isActive = record.individual.active;
                const lastLogin = record.individual.lastLogin;
                const formattedDate = moment(lastLogin).format('DD/MM/YY, hh:mm A');
                return (
                    <div data-testid={dataTestId}>
                        <StatusTag
                            color={isActive ? 'var(--success-normal)' : 'var(--error-normal)'}
                            status={isActive ? 'Active' : 'Deactivated'}
                        />
                        {formattedDate}
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'ACTIONS',
            render: (_, record) => {
                const dataTestId = 'members-table-actions-row';
                return (
                    <Space size={8} data-testid={dataTestId}>
                        <IconButton icon={<EditOutlined />} tooltipText="Edit" onClick={() => handleEdit(record)} />
                        <IconButton
                            icon={<ContainerOutlined />}
                            tooltipText="Archive"
                            // onClick={() => handleArchive(record.individual.id)}
                        />
                        {onRemoveTeamMember && (
                            <IconButton
                                icon={<DeleteOutlined />}
                                tooltipText="Delete"
                                onClick={() => onRemoveTeamMember(record.individual.id)}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const handleEdit = (teamMemberResponse: TeamMemberResponse) => {
        setOpen({ key: 'edit-team-member-drawer', data: { teamMemberResponse } });
    };

    const [open, setOpen] = useState<{
        key: 'edit-team-member-drawer';
        data: { teamMemberResponse: TeamMemberResponse };
    } | null>(null);

    const isOpen = (key: NonNullable<typeof open>['key']) => open?.key === key;

    const filteredByAccessColumns = !hasPermissions(userInfo, 'teamMembers', 'UPDATE')
        ? allColumns.filter((col) => col.key !== 'ACTIONS')
        : allColumns;

    return (
        <StyledTableWrapper>
            <Table<TeamMemberResponse>
                rowKey="id"
                dataSource={teams}
                columns={filteredByAccessColumns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />

            <EditTeamMemberDrawer
                open={isOpen('edit-team-member-drawer')}
                onClose={() => setOpen(null)}
                teamMemberResponse={open?.data.teamMemberResponse}
                onSuccess={() => {
                    onEditTeamMember?.();
                }}
            />
        </StyledTableWrapper>
    );
};

const EditTeamMemberDrawer = (
    props: DrawerProps & { teamMemberResponse: TeamMemberResponse | undefined; onSuccess?: () => void }
) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);

    const originalRole = props.teamMemberResponse?.role;

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: props.teamMemberResponse?.individual.fullName,
            email: props.teamMemberResponse?.individual.email,
            role: props.teamMemberResponse?.role,
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required('Name is required'),
            email: Yup.string().email('Invalid email').required('Email is required'),
            role: Yup.string().required('Role is required'),
        }),
        onSubmit: async (values, helpers) => {
            if (!props.teamMemberResponse) return;

            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });

                if (!values.role || !values.name || !values.email) {
                    throw new Error('No role selected.');
                }

                await editIndividual(props.teamMemberResponse.individual.id, {
                    ...(props.teamMemberResponse.individual as any),
                    fullName: values.name,
                    email: values.email,
                    type: values.role as any,
                });

                Notification({
                    placement: 'top',
                    type: 'success',
                    messageTitle: 'Success',
                    description: 'Successfully edited team member',
                });

                helpers.resetForm();
                props.onSuccess?.();
                props.onClose?.(null as any);
            } catch (error) {
                const errorInfo = error instanceof AxiosError && error?.response?.data?.message;
                Notification({
                    placement: 'top',
                    type: 'error',
                    messageTitle: 'Error',
                    description: errorInfo || 'Could not edit team member. Please try again.',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const roleChanged = !!originalRole && !!formik.values.role && formik.values.role !== originalRole;

    const handleEditClick = async () => {
        const errors = await formik.validateForm();
        formik.setTouched({ name: true, email: true, role: true }, true);

        if (Object.keys(errors).length > 0) return;

        if (roleChanged) {
            setPendingSubmit(true);
            setShowConfirmationModal(true);
            return;
        }

        formik.submitForm();
    };

    return (
        <>
            <Drawer
                open={true}
                title="New Member"
                maskClosable={false}
                width={500}
                extra={
                    <Space>
                        <Button onClick={props.onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={!formik.isValid}
                            loading={formik.isSubmitting}
                            onClick={handleEditClick}
                        >
                            Edit
                        </Button>
                    </Space>
                }
                {...omit(props, ['teamMemberResponse', 'onSuccess'])}
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <label>
                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                        Name
                        <FormInput
                            placeholder="Enter member name"
                            fieldProps={formik.getFieldProps('name')}
                            fieldMeta={formik.getFieldMeta('name')}
                            fieldHelper={formik.getFieldHelpers('name')}
                        />
                    </label>
                    <label>
                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                        Email
                        <FormInput
                            placeholder="Enter email"
                            fieldProps={formik.getFieldProps('email')}
                            fieldMeta={formik.getFieldMeta('email')}
                            fieldHelper={formik.getFieldHelpers('email')}
                        />
                    </label>
                    <label>
                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                        Role
                        <FormSelect
                            hideKeys
                            placeholder="Select role"
                            fieldHelper={formik.getFieldHelpers('role')}
                            fieldMeta={formik.getFieldMeta('role')}
                            fieldProps={formik.getFieldProps('role')}
                            selectOptions={ALLOWED_NEW_MEMBER_ROLES.map((role) => ({
                                id: role,
                                value: startCase(toLower(role)),
                            }))}
                        />
                    </label>
                </Space>
            </Drawer>

            <ConfirmTeamRoleChangeModal
                open={showConfirmationModal}
                onConfirm={() => {
                    setShowConfirmationModal(false);
                    if (pendingSubmit) {
                        setPendingSubmit(false);
                        formik.submitForm();
                    }
                }}
                onClose={() => {
                    setShowConfirmationModal(false);
                    setPendingSubmit(false);
                }}
            />
        </>
    );
};

export default TeamMembersTable;
